import { memo, useCallback, useRef, useState } from 'react';
import { Emoji } from './Emoji';
import { cn, hashString, type Side, TEAM_COLOR } from './util';

export interface FloatingReaction {
  id: string;
  emoji: string;
  /** Horizontal start position 0..1 (default: pseudo-random from the id). */
  x?: number;
  /** Optional sender name tag. */
  by?: string;
  /** Sender team, colours the name tag. */
  team?: Side;
}

export interface FloatingReactionsProps {
  items: readonly FloatingReaction[];
  /** Called when an emoji finished floating — remove it from the queue. */
  onDone: (id: string) => void;
  /** Emoji size in px. */
  size?: number;
  className?: string;
}

/** One floating emoji. Memoised: the list re-renders on every push, each item is static. */
const Floater = memo(function Floater({
  item: r,
  size,
  onDone,
}: {
  item: FloatingReaction;
  size: number;
  onDone: (id: string) => void;
}) {
  const h = hashString(r.id);
  const x = r.x ?? 0.12 + ((h % 1000) / 1000) * 0.76;
  const dur = 2.1 + ((h >> 10) % 7) / 10;
  return (
    <div
      // data-motion-keep: under reduced motion the CSS swaps the drift for an opacity-only fade
      // (same duration) instead of removing the emoji instantly.
      data-motion-keep
      className="animate-float-up absolute bottom-[6%] flex flex-col items-center will-change-transform"
      style={{ left: `${x * 100}%`, animationDuration: `${dur}s` }}
      onAnimationEnd={(e) => e.target === e.currentTarget && onDone(r.id)}
    >
      <Emoji
        emoji={r.emoji}
        label=""
        size={size}
        className="drop-shadow-[2px_3px_0_rgb(17_17_17/0.25)]"
      />
      {r.by && (
        <span
          className="mt-0.5 border-2 border-ink bg-white px-1.5 text-[11px] leading-tight font-black whitespace-nowrap"
          style={r.team ? { color: TEAM_COLOR[r.team] } : undefined}
        >
          {r.by}
        </span>
      )}
    </div>
  );
});

/**
 * Overlay that floats reaction emoji up and fades them out. Place inside a `relative` container
 * (the arena). Pointer-events pass through. Pass a stable `onDone` (e.g. `useReactionQueue().remove`)
 * so items don't re-render.
 */
export function FloatingReactions({ items, onDone, size = 40, className }: FloatingReactionsProps) {
  return (
    <div
      aria-hidden
      className={cn('pointer-events-none absolute inset-0 z-30 overflow-hidden', className)}
    >
      {items.map((r) => (
        <Floater key={r.id} item={r} size={size} onDone={onDone} />
      ))}
    </div>
  );
}

/** Local queue for `FloatingReactions` (caps concurrent emoji). */
export function useReactionQueue(max = 30) {
  const [items, setItems] = useState<FloatingReaction[]>([]);
  const seq = useRef(0);
  const push = useCallback(
    (emoji: string, extra: Omit<FloatingReaction, 'id' | 'emoji'> = {}) => {
      seq.current += 1;
      const id = `r${seq.current}-${Math.random().toString(36).slice(2, 7)}`;
      setItems((prev) => [...prev, { id, emoji, ...extra }].slice(-max));
    },
    [max],
  );
  const remove = useCallback(
    (id: string) => setItems((prev) => prev.filter((r) => r.id !== id)),
    [],
  );
  return { items, push, remove };
}
